/**
 * Pure page-range math for the document-splitting UI. Ported verbatim from
 * glynt-ui-next/src/components/page-viewer/page-ranges.ts — no UI deps, it is
 * the heart of the boundary→page-group conversion. Keep in sync with the
 * source app; the request contract depends on this behaving identically.
 */

/**
 * Convert boundary page numbers into inclusive [start, end] page ranges.
 * Boundaries are the pages where a NEW child starts; first child always starts at 1
 * and last child always ends at pageCount. Boundaries are de-duplicated and sorted.
 */
export function boundariesToPageRanges(boundaries: number[], pageCount: number): number[][] {
  const sorted = Array.from(new Set(boundaries))
    .filter((b) => b > 1 && b <= pageCount)
    .sort((a, b) => a - b);
  const ranges: number[][] = [];
  let start = 1;
  for (const boundary of sorted) {
    ranges.push([start, boundary - 1]);
    start = boundary;
  }
  ranges.push([start, pageCount]);
  return ranges;
}

/**
 * Compute boundaries for the "split every N pages" shortcut.
 * For N=3 over 10 pages, returns [4, 7, 10] — i.e. children of size 3, 3, 3, 1.
 * N=1 puts every page in its own child.
 * Returns [] for N < 1 or N >= pageCount (no useful split possible).
 */
export function periodicBoundaries(everyN: number, pageCount: number): number[] {
  if (!Number.isInteger(everyN) || everyN < 1 || everyN >= pageCount) {
    return [];
  }
  const out: number[] = [];
  for (let p = everyN + 1; p <= pageCount; p += everyN) {
    out.push(p);
  }
  return out;
}

/** True iff `order` is the identity [1, 2, …, n]. */
export function isIdentityOrder(order: number[]): boolean {
  for (let i = 0; i < order.length; i++) {
    if (order[i] !== i + 1) return false;
  }
  return true;
}

/**
 * Compact per-child range string for the split-page header summary.
 * - Contiguous identity-ordered groups collapse to "a-b".
 * - Singletons render as just "a".
 * - Non-contiguous groups (after reorder) list their pages as "a,b,c".
 * - Empty groups (e.g. wholly-omitted children) are skipped.
 *   formatChildrenSummary([[1,2,3]])         → "1-3"
 *   formatChildrenSummary([[1,2],[3,4,5]])   → "1-2 · 3-5"
 *   formatChildrenSummary([[4],[1,2]])       → "4 · 1-2"
 *   formatChildrenSummary([[3,1,2]])         → "3,1,2"
 *   formatChildrenSummary([[],[1,2]])        → "1-2"
 */
export function formatChildrenSummary(groups: number[][]): string {
  return groups
    .filter((g) => g.length > 0)
    .map((g) => {
      if (g.length === 1) return `${g[0]}`;
      const isContiguous = g.every((p, i) => i === 0 || p === g[i - 1] + 1);
      return isContiguous ? `${g[0]}-${g[g.length - 1]}` : g.join(',');
    })
    .join(' · ');
}

/**
 * Convert visible-position boundaries + a pageOrder permutation into the
 * per-child page sequences that will be sent to the backend.
 *
 *   boundariesToPageGroups([3], [1,2,3,4,5]) → [[1,2], [3,4,5]]
 *   boundariesToPageGroups([3], [3,1,2,4,5]) → [[3,1], [2,4,5]]
 *   boundariesToPageGroups([],  [3,1,2,4,5]) → [[3,1,2,4,5]]
 *
 * Boundaries are de-duplicated, sorted, and clamped to (1, pageOrder.length].
 */
export function boundariesToPageGroups(boundaries: number[], pageOrder: number[]): number[][] {
  const n = pageOrder.length;
  const sorted = Array.from(new Set(boundaries))
    .filter((b) => b > 1 && b <= n)
    .sort((a, b) => a - b);
  const groups: number[][] = [];
  let start = 1;
  for (const boundary of sorted) {
    groups.push(pageOrder.slice(start - 1, boundary - 1));
    start = boundary;
  }
  groups.push(pageOrder.slice(start - 1));
  return groups;
}
